import { date } from 'quasar'
import Activity, { SESSION_ADMISSION_TYPES } from 'src/models/activity/Activity'
import ActivitySession from 'src/models/activity-session/ActivitySession'
import Model, { encoders, ID, IsoDateTime, LocalizedJsonObject } from 'src/models/model'
import Order, { getAncestorOrder } from 'src/models/order/Order'
import OrderItem, { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import activitySessionResolver from 'src/services/activity-session/ActivitySessionResolver'

const { addToDate, formatDate } = date

export const ACTIVITY_BOOKING_STEPS = {
  TICKETS: 'tickets',
  DATE_AND_TIME: 'date-and-time',
  ADD_ONS: 'add-ons'
}

export const BOOKING_STATES = {
  DRAFT: 'draft',
  RESERVATION: 'reservation',
  PARTIALLY_VOIDED: 'partially_voided',
  VOIDED: 'voided'
}

export default class ActivityBooking extends Model {
  static name = 'ActivityBooking'

  static paginate = true

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      state: {
        type: String,
        default: 'draft'
      },
      activity: {
        type: Activity,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      order: {
        type: Order,
        reference: true,
        output: true,
        computed () {
          return getAncestorOrder(this) || this.data?.order
        }
      },
      activitySession: {
        type: ActivitySession,
        default: null,
        encode: encoders.hasOne
      },
      activityUnitQuantity: {
        type: Number,
        default: 1
      },
      activitySessionEntryTime: {
        type: IsoDateTime,
        default: null
      },
      activitySessionExitTime: {
        type: IsoDateTime,
        default: null
      },
      config: {
        type: LocalizedJsonObject,
        mergeDefault: true,
        default () {
          return {
            createKey: null,
            participantDetails: [],
            selectedActivitySessionDate: null,
            selectedActivitySpaceId: null
          }
        }
      },
      isActivated: {
        type: Boolean,
        default: false,
        local: true
      }
    }

    this.computed = {
      isDraft () {
        return this.state === 'draft'
      },
      orderState () {
        return this.order?.state
      },
      activityTicketOrderItems () {
        return _(this.order?.activityTicketOrderItems).filter({ activityTicket: { activityBookingId: this.id } }).value()
      },
      activitySalesItemOrderItems () {
        return _(this.order?.activitySalesItemOrderItems).filter({ activityBookingId: this.id }).value()
      },
      activitySalesItemOrderItemsCountByItemVariationId () {
        return _(this.activitySalesItemOrderItems)
          .groupBy(orderItem => orderItem.itemOrderItem.itemVariationId)
          .mapValues(items => items.length)
          .value()
      },
      itemPackageActivityTicketOrderItems () {
        return _(this.order?.itemPackageActivityTicketOrderItems).filter({ activityTicket: { activityBookingId: this.id } }).value()
      },
      nonItemPackageActivityTicketOrderItems () {
        return _(this.order?.activityTicketOrderItems).filter(orderItem => {
          return _.isNil(orderItem.itemPackageId) && orderItem.activityTicket.activityBookingId === this.id
        }).value()
      },
      numSelectedTickets () {
        return this.activityTicketOrderItems.length
      },
      areMinimumAttendanceRequirementsMet () {
        return this.numSelectedTickets > 0 && (
          this.activity.sessionAdmissionType === SESSION_ADMISSION_TYPES.SESSION ||
          !this.activity.isExclusive ||
          !this.activity.hasMinimumAttendance ||
          this.numSelectedTickets >= this.activity.minimumAttendance
        )
      },
      allowDraftCleanup () {
        return this.isDraft && !this.areMinimumAttendanceRequirementsMet
      },
      guestItemPackages () {
        return this.activity.guestItemPackages || []
      },
      guestTicketTypes () {
        return this.activity.guestTicketTypes || []
      },
      guestTicketTypesOrItemPackages () {
        return _.concat(this.guestTicketTypes, this.guestItemPackages)
      },
      isTicketLimitReached () {
        return this.numSelectedTickets >= this.activity.maxActivitySpaceCapacity
      },
      hasTicketTypesOrItemPackagesSelected () {
        return this.numSelectedTickets > 0
      },
      siteEntryTime () {
        if (_.isNil(this.activitySessionEntryTime)) return this.activitySessionEntryTime

        return this.activitySessionEntryTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      siteExitTime () {
        if (_.isNil(this.activitySessionExitTime)) return this.activitySessionExitTime

        return this.activitySessionExitTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      activityName () {
        return `${this.activity.name} (${this.i18n.t('activityBooking.bookingId', { bookingId: this.id })})`
      },
      activityUnitQuantityDescription () {
        if (this.activity.sessionAdmissionType === SESSION_ADMISSION_TYPES.SESSION || this.activity.maxConsecutiveActivityUnits === 1) {
          return null
        } else {
          return `${this.activityUnitQuantity} ${this.activityUnitQuantity > 1 ? this.activity.activityUnitDescriptionPlural : this.activity.activityUnitDescription}`
        }
      },
      activitySpaceDescription () {
        return this.activitySession?.activitySpace?.name || null
      },
      isComplete () {
        return (
          !this.loading &&
          this.areMinimumAttendanceRequirementsMet &&
          !_.isNil(this.activitySession) &&
          !_.isNil(this.activitySessionEntryTime)
        )
      },
      incompleteErrors () {
        const errors = []

        if (!this.areMinimumAttendanceRequirementsMet) {
          errors.push(this.i18n.t('activityBooking.errors.minimumAttendanceRequirementsNotMet'))
        }

        if (_.isNil(this.activitySession)) {
          errors.push(this.i18n.t('activityBooking.errors.noDateAndTimeSelected'))
        }

        return errors
      },
      bookingUserName () {
        return this.order?.nameForOrder || `${this.order?.user?.firstName ?? ''} ${this.order?.user?.lastName ?? ''}`.trim() || this.i18n.t('activityBooking.guest.name')
      }
    }
  }

  activate () {
    this.isActivated = true
  }

  formattedTime (showDate = false, relative = false) {
    if (_.isNil(this.siteEntryTime)) {
      return null
    }

    const today = formatDate(new Date(), 'YYYY-MM-DD')
    const tomorrow = formatDate(addToDate(new Date(), { days: 1 }), 'YYYY-MM-DD')

    const sessionDate = formatDate(this.siteEntryTime, 'YYYY-MM-DD')

    const entryTime = formatDate(this.siteEntryTime, 'h:mm a')
    const exitTime = formatDate(this.siteExitTime, 'h:mm a')

    if (!showDate) {
      return `${entryTime} until ${exitTime}`
    }

    let date = ''

    if (relative && sessionDate === today) {
      date = this.i18n.t('time.today')
    } else if (relative && sessionDate === tomorrow) {
      date = this.i18n.t('time.tomorrow')
    } else {
      date = formatDate(this.siteEntryTime, 'MMM Do')
    }

    return `${date} from ${entryTime} until ${exitTime}`
  }

  getFromPrice (ticketTypeOrItemPackage) {
    const fromPriceKey = ticketTypeOrItemPackage.typeName === 'ItemPackage' ? 'item_packages' : 'ticket_types'
    return this.activity.fromPrices?.[fromPriceKey]?.[ticketTypeOrItemPackage.id]
  }

  guestTicketTypeOrItemPackageQuantity (ticketTypeOrItemPackage) {
    if (ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      const numTicketTypesInItemPackage = _(ticketTypeOrItemPackage.itemPackageItems.data).filter({ itemType: ORDER_ITEM_TYPES.TICKET }).value().length
      return _(this.itemPackageActivityTicketOrderItems).filter({ itemPackage: { id: ticketTypeOrItemPackage.id }, activityTicket: { user: null } }).value().length / numTicketTypesInItemPackage
    } else {
      return _(this.nonItemPackageActivityTicketOrderItems).filter({ activityTicket: { user: null, ticketType: { id: ticketTypeOrItemPackage.id } } }).value().length
    }
  }

  isGuestTicketTypeOrItemPackageSelected (ticketTypeOrItemPackage) {
    return !!this.guestTicketTypeOrItemPackageQuantity(ticketTypeOrItemPackage)
  }

  isUserTicketTypeOrItemPackageSelected (userOrderData) {
    if (userOrderData.ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      return _.some(this.itemPackageActivityTicketOrderItems, { itemPackage: { id: userOrderData.ticketTypeOrItemPackage.id }, activityTicket: { user: { id: userOrderData.user.id } } })
    } else {
      return _.some(this.nonItemPackageActivityTicketOrderItems, { activityTicket: { user: { id: userOrderData.user.id }, ticketType: { id: userOrderData.ticketTypeOrItemPackage.id } } })
    }
  }

  increment (ticketTypeOrItemPackage, user = null) {
    if (ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      this.order.createItemPackageOrderItemsForActivityAndAddToOrder(ticketTypeOrItemPackage, this.id, { user })
    } else {
      this.order.createActivityTicketOrderItemAndAddToOrder(this.id, ticketTypeOrItemPackage.id, { user })
    }

    this.resetActivitySessionData()
  }

  decrement (ticketTypeOrItemPackage, user = null) {
    if (ticketTypeOrItemPackage.typeName === 'ItemPackage') {
      this.order.removeItemPackageOrderItemsForActivity(ticketTypeOrItemPackage, this.id, { user })
    } else {
      const [lastAddedOrderItem] = _(this.nonItemPackageActivityTicketOrderItems).filter({ activityTicket: { ticketType: { id: ticketTypeOrItemPackage.id } } }).reverse().value()

      if (lastAddedOrderItem) {
        this.order.removeOrderItem(lastAddedOrderItem)
      }
    }

    this.resetActivitySessionData()
  }

  incrementActivityUnitQuantity () {
    if (this.activityUnitQuantity < this.activity.maxConsecutiveActivityUnits) {
      this.activityUnitQuantity++
      this.resetActivitySessionData()
    }
  }

  decrementActivityUnitQuantity () {
    if (this.activityUnitQuantity > 1) {
      this.activityUnitQuantity--
      this.resetActivitySessionData()
    }
  }

  incrementSalesItem (itemVariation) {
    const orderItem = new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: itemVariation.item.id,
        itemVariationId: itemVariation.id,
        itemModifiers: []
      },
      voucher: null,
      voucherType: null,
      discount: null,
      activityBookingId: this.id
    })

    this.order.orderItems.add(orderItem)
    orderItem.order = this.order
  }

  decrementSalesItem (itemVariation) {
    const [lastAddedOrderItem] = _(this.activitySalesItemOrderItems).filter({ itemOrderItem: { itemVariationId: itemVariation.id } }).reverse().value()

    if (lastAddedOrderItem) {
      this.order.removeOrderItem(lastAddedOrderItem)
    }
  }

  salesItemCount (itemVariation) {
    return this.activitySalesItemOrderItemsCountByItemVariationId[itemVariation.id] || 0
  }

  async selectActivitySessionDate (selectedDate) {
    const selectedDateFormatted = selectedDate?.replace(/-/g, '/')

    if (_.isNil(selectedDate) || selectedDateFormatted === this.config.selectedActivitySessionDate) return

    this.config.selectedActivitySessionDate = selectedDateFormatted
    this.resetActivitySessionData()

    await this.order.save()
  }

  resetActivitySessionData () {
    if (this.config) {
      this.config.selectedActivitySpaceId = null
    }

    this.activitySession = null
    this.activitySessionEntryTime = null
    this.activitySessionExitTime = null
  }

  /**
   * activitySessionsAndAvailableEntries (example):
   * [
   *  {
   *    activitySessionId: 1,
   *    activitySessionEndTime: '2025-01-01T10:00:00Z',
   *    activitySpaceId: 1,
   *    availableEntries: ['2025-01-01T09:00:00Z', '2025-01-01T10:00:00Z', '2025-01-01T11:00:00Z']
   *  }
   * ]
   */

  async selectActivitySessionEntryTime (selectedEntryTime, activitySessionsAndAvailableEntries) {
    const { activitySession, activitySessionEntryTime, activitySessionExitTime } = activitySessionResolver.resolve(
      this,
      selectedEntryTime,
      activitySessionsAndAvailableEntries
    )

    this.activitySession = activitySession
    this.activitySessionEntryTime = activitySessionEntryTime
    this.activitySessionExitTime = activitySessionExitTime

    await this.order.save()
  }

  async toggleSelectedActivitySpace (activitySpace, activitySessionsAndAvailableEntries) {
    if (this.config.selectedActivitySpaceId === activitySpace.id) {
      this.config.selectedActivitySpaceId = null
    } else {
      this.config.selectedActivitySpaceId = activitySpace.id
    }

    const { activitySession, activitySessionEntryTime, activitySessionExitTime } = activitySessionResolver.resolve(
      this,
      this.activitySessionEntryTime,
      activitySessionsAndAvailableEntries
    )

    this.activitySession = activitySession
    this.activitySessionEntryTime = activitySessionEntryTime
    this.activitySessionExitTime = activitySessionExitTime

    await this.order.save()
  }

  isEntryTimeAvailable (entryTime, availableEntries) {
    return _(availableEntries).map(entry => new IsoDateTime(entry).toString()).includes(entryTime.toString())
  }

  isEntryTimeDisabled (entryTime, activitySessionsAndAvailableEntries) {
    if (_.isNil(this.config.selectedActivitySpaceId)) return false

    const availableEntriesForSelectedActivitySpace = this.allAvailableEntryTimes(
      _(activitySessionsAndAvailableEntries).filter({ activitySpaceId: this.config.selectedActivitySpaceId }).value()
    )

    return !this.isEntryTimeAvailable(entryTime, availableEntriesForSelectedActivitySpace)
  }

  isActivitySpaceDisabled (activitySpace, activitySessionsAndAvailableEntries) {
    if (_.isNil(this.activitySessionEntryTime)) return false

    const availableEntriesForActivitySpace = this.allAvailableEntryTimes(
      _(activitySessionsAndAvailableEntries).filter({ activitySpaceId: activitySpace.id }).value()
    )

    return !this.isEntryTimeAvailable(this.activitySessionEntryTime, availableEntriesForActivitySpace)
  }

  allAvailableEntryTimes (activitySessionsAndAvailableEntries) {
    return _(activitySessionsAndAvailableEntries)
      .map('availableEntries')
      .flatten()
      .map(dateString => new IsoDateTime(dateString))
      .uniqBy(dateTime => dateTime.i18n('shortTime'))
      .sortBy(date => date.getTime())
      .value()
  }

  entryTimeOptions (activitySessionsAndAvailableEntries) {
    return _(this.allAvailableEntryTimes(activitySessionsAndAvailableEntries))
      .map(dateTime => ({
        label: dateTime.inTimeZone(this.configuration.currentSite.timeZone).i18n('shortTime'),
        value: dateTime,
        disable: this.isEntryTimeDisabled(dateTime, activitySessionsAndAvailableEntries)
      }))
      .value()
  }

  allActivitySpaces (activitySessionsAndAvailableEntries) {
    const activitySpaceIdsFromActivitySessions = _(activitySessionsAndAvailableEntries).map('activitySpaceId').uniq().value()
    return _(this.configuration.activitySpaces.data).filter(({ id }) => activitySpaceIdsFromActivitySessions.includes(id)).uniqBy('id').value()
  }
}
